import { CallBase, CallState, RecordResponse, OnMatchRecord, RecordCond, RCXLogger } from 'rcx_adapter';
import { updateSessionData } from 'rcx_adapter/src/rules';
import { CallEventType, OnNewCall, ParamsType, RecordType } from 'rcx_adapter/src/typing';

import { BaseModule } from './BaseModule';
import PopRecordModule from './PopRecord';
import { LOGGING_TYPE } from '../constants';
import { ServiceNowAdapterEvent } from '../enums';
import { ServiceNowEntity } from '../enums/ServiceNowEntity';
import { ModuleBaseOption } from '../interfaces/module';
import { ruleConfigsForIncidentVoice, ruleConfigsForInteractionVoice } from '../rules/ServiceNowRuleConfigs';
interface Click2Dial {
    entity: string;
    label: string;
    phoneNumber?: string;
    query: string;
    value: string;
}

class OnCallMatchModule extends BaseModule<ModuleBaseOption> {
    private currentCallId: string = '';
    public clickToDial!: Click2Dial | null;
    public start(): void {
        if (this.getIsInit()) {
            this.listenFromRCX();
        }
    }
    private listenFromRCX() {
        this.mRCXAdapter!.onNewCall(this.onNewCall.bind(this));
        this.mRCXAdapter!.onMatchingRecords((data: OnMatchRecord) => {
            if (data.type === 'Call') {
                const callLoggingType = this.mServiceNowAdapter!.getCallLoggingType();
                const callBaseData = { ...(data.params as CallBase), callLoggingType };
                updateSessionData(
                    callBaseData,
                    data.type,
                    callLoggingType === LOGGING_TYPE.INCIDENT
                        ? ruleConfigsForIncidentVoice
                        : ruleConfigsForInteractionVoice
                );
                return this.onMatchingRecords(callBaseData);
            }
        });
        this.mRCXAdapter!.onSearchRecord(this.onSearch.bind(this));
        this.mServiceNowAdapter!.on(ServiceNowAdapterEvent.CLICK_TO_DIAL, (data: any) => {
            this.setClickToDialNumber({
                phoneNumber: data[0].data.metaData.phoneNumber,
                ...data[0].data.data[0],
            });
        });
    }

    public onNewCall(data: OnNewCall) {
        this.currentCallId = data.uii;
    }

    public async onMatchingRecords(data: CallBase): Promise<RecordResponse> {
        const adapter = this.mServiceNowAdapter!;
        const logAdapter = adapter.getLogAdapter();
        const loggingType = adapter.getCallLoggingType();
        const isActiveCall = this.isActiveCall(data);
        logAdapter.resetLogRule(data.uii);

        if (loggingType !== LOGGING_TYPE.INCIDENT || !isActiveCall) {
            return this.onDefaultMatchingRecords(data);
        }

        const matchingResult = data?.sessionData?.cx?.crm?.matchingResult;
        RCXLogger.getInstance().info(
            `[ServiceNow][OnCallMatchModule] IVR match result: [${matchingResult?.join(', ')}] records found`
        );

        if (!matchingResult?.length) {
            return this.onDefaultMatchingRecords(data);
        }

        const incidentResult = matchingResult.find(
            (item) => item?.crmEntityType?.toLowerCase() === RecordType.INCIDENT.toLowerCase()
        );
        if (incidentResult) {
            const validMatchedIds = incidentResult?.crmMatchedIds?.filter(Boolean) || [];

            if (validMatchedIds.length === 0) {
                return this.onDefaultMatchingRecords(data);
            }

            if (validMatchedIds.length === 1) {
                return this.onSingleIncidentFromIVR(validMatchedIds, data.uii);
            }

            // multiple incident match
            logAdapter.setLogRule(data.uii, {
                isCallLogDisabled: true,
                callLogSysId: '',
            });
            return { records: [] };
        }

        // non-incident match
        logAdapter.setLogRule(data.uii, {
            isCallLogDisabled: true,
            callLogSysId: '',
        });

        return { records: [] };
    }

    public async onSearch(data: RecordCond) {
        const { type = '', value } = data || {};

        if (!value) {
            return Promise.reject('value is empty');
        }
        let fields;
        if (type === 'name') {
            fields = {
                name: value,
            };
        } else {
            fields = {
                u_phone: value,
                u_email: value,
                number: value,
            };
        }
        const response = await this.mServiceNowAdapter!.getCustomMatch(type, fields, ParamsType.CALL);
        return {
            records: response,
        };
    }

    private async onDefaultMatchingRecords(data: CallBase): Promise<RecordResponse> {
        const { sessionData } = data ?? {};
        const matchingRules = sessionData?.cx?.crm?.matchingRules || [];

        const records = await Promise.all(
            matchingRules.map(async (rule) => {
                const response = await this.mServiceNowAdapter!.getStandMatch(
                    rule.crmEntityValue,
                    rule.crmEntityIdName,
                    rule.crmEntityType,
                    ParamsType.CALL
                );
                if (response) {
                    return response;
                }
                return null;
            }) ?? []
        );
        let filteredRecords = records
            .filter((record): record is NonNullable<typeof record> => record != null)
            .flatMap((record) => record);
        filteredRecords = this.addCurrentFlag(filteredRecords);
        PopRecordModule.onMatchRecords(filteredRecords, data);
        return {
            records: filteredRecords,
        };
    }

    private async onSingleIncidentFromIVR(validMatchedIds: string[], uii: string) {
        const adapter = this.mServiceNowAdapter!;
        const logAdapter = adapter.getLogAdapter();
        const sysId = validMatchedIds[0];
        logAdapter.setLogRule(uii, {
            isCallLogDisabled: false,
            callLogSysId: sysId,
        });
        adapter.openFrameOpenServiceNowFormBySysId(sysId, ServiceNowEntity.INCIDENT);
        return { records: [] };
    }

    private setClickToDialNumber(data: Click2Dial) {
        this.clickToDial = data;
    }

    private isValidMatchType() {
        const key = this.clickToDial?.entity || '';
        return Boolean(Object.values(ServiceNowEntity).includes(key as ServiceNowEntity));
    }

    private isMatchedClickToDial(id: string): boolean {
        if (!this.clickToDial) {
            return false;
        }
        if (!(!!id || this.isValidMatchType())) {
            return false;
        }
        return this.clickToDial.query.split('=')[1] === id;
    }

    private addCurrentFlag(responseData: any) {
        const response = responseData.map((item: any) => {
            item.isCurrent = this.isMatchedClickToDial(item.id);
            return item;
        });
        this.clickToDial = null;
        return response;
    }

    private isActiveCall(call: CallBase) {
        return (
            (call.callState === CallState.ACTIVE || call.type === CallEventType.NEW_CALL) &&
            call.uii === this.currentCallId
        );
    }
}

export default new OnCallMatchModule();
